import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Grid,
  Alert,
  TextField,
} from '@mui/material';
import {
  useRouter,
  PageHeader,
  PageContent,
  FormBottomBar,
  FormErrorDialog,
  useFormError,
  useNotification,
} from '@viilutalous/common';
import { categoriesAPI } from './expensesAPI';

export default function ExpenseCategoryForm({ tenantId, setBreadcrumbTitle }) {
  const { navigate, params } = useRouter();
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const { error: formError, dialogOpen: errorDialogOpen, handleApiError, showError, closeDialog } = useFormError();
  const { id } = params;
  const isNew = id === 'new' || !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, setValue, trigger, watch, formState: { errors } } = useForm({
    defaultValues: {
      code: '',
      name: '',
      unit_price: 0,
      unit: 'unit',
    },
  });

  const nameValue = watch('name');

  useEffect(() => {
    return () => {
      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(null);
      }
    };
  }, [setBreadcrumbTitle]);

  useEffect(() => {
    if (tenantId && !isNew) {
      fetchCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const response = await categoriesAPI.getOne(tenantId, id);
      const cat = response.data;

      setValue('code', cat.code || '');
      setValue('name', cat.name || '');
      setValue('unit_price', cat.unit_price ?? 0);
      setValue('unit', cat.unit || 'unit');

      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(cat.name);
      }
    } catch (error) {
      console.error('Error fetching expense category:', error);
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const payload = {
        code: data.code.trim(),
        name: data.name.trim(),
        unit_price: parseFloat(data.unit_price) || 0,
        unit: data.unit.trim(),
      };

      if (isNew) {
        await categoriesAPI.create(tenantId, payload);
        showSuccess(t('expense_categories.created'));
      } else {
        await categoriesAPI.update(tenantId, id, payload);
        showSuccess(t('expense_categories.updated'));
      }
      navigate('/expense-categories');
    } catch (error) {
      console.error('Error saving expense category:', error);
      handleApiError(error, t);
    } finally {
      setSaving(false);
    }
  };

  const onSubmitError = (formErrors) => {
    const errorMessages = [];
    Object.entries(formErrors).forEach(([, error]) => {
      if (error?.message) {
        errorMessages.push(error.message);
      }
    });
    if (errorMessages.length > 0) {
      showError(errorMessages.join('\n'));
    }
  };

  const handleSaveClick = async (e) => {
    if (e) e.preventDefault();
    const isValid = await trigger();
    if (!isValid) {
      onSubmitError(errors);
      return;
    }
    handleSubmit(onSubmit, onSubmitError)(e);
  };

  const handleCancel = () => {
    navigate('/expense-categories');
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('expense_categories.select_tenant')}</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        <Paper sx={{ p: 3, maxWidth: 800 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('expense_categories.code')}
                {...register('code', { required: t('expense_categories.error.code_required') })}
                error={!!errors.code}
                helperText={errors.code?.message}
                required
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('expense_categories.name')}
                {...register('name', { required: t('expense_categories.error.name_required') })}
                error={!!errors.name}
                helperText={errors.name?.message}
                required
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('expense_categories.unit_price')}
                type="number"
                {...register('unit_price')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('expense_categories.unit')}
                {...register('unit')}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Paper>
      </PageContent>

      <FormBottomBar
        onSave={handleSaveClick}
        onCancel={handleCancel}
        saving={saving}
        disabled={!nameValue?.trim()}
      />

      <FormErrorDialog
        open={errorDialogOpen}
        onClose={closeDialog}
        message={formError?.message}
      />
    </>
  );
}
